"use client";

import * as Dialog from "@radix-ui/react-dialog";
import useSWR from "swr";
import { useEffect, useState } from "react";
import { fetcher, handle429s } from "@utils/fetchers";
import debounce from "lodash/debounce";
import Image from "next/image";
import {
  ArrowRightIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { MegaphoneIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { AnimatePresence, animate, motion } from "framer-motion";
import LoadingSpinner from "./loadingSpinner";
import { useRouter } from "next/navigation";
import { joinClassNames } from "@utils/helpers";
import { useWindowSize } from "@utils/hooks";
import { useTranslations } from "next-intl";
import LandingPageLinkV2 from "@components/links/landingPageLinkV2";

interface LandingPage {
  Name: string;
  Url: string;
  ImageUrl: string;
}

interface Event {
  Name: string;
  Url: string;
  ImageUrl: string;
  Date: string;
  Venue: string;
}

interface Props {
  in_jumbotron?: boolean;
  icon?: React.ReactNode;
  onClick?: () => void;
}

export default function SearchResults({ in_jumbotron, icon, onClick }: Props) {
  const router = useRouter();
  const windowSize = useWindowSize();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const t = useTranslations("Search");

  const { data: results, isLoading } = useSWR<{
    Query: string;
    Events: Event[];
    LandingPages: LandingPage[];
  }>(query ? `search/quick/${query}` : null, fetcher, {
    onErrorRetry: handle429s,
    keepPreviousData: query ? true : false,
  });

  const debouncedSetQuery = debounce((q: string) => setQuery(q), 250);

  function getRelativePath(url: string) {
    const rel_path = `/events/${url.split("/events/")[1]}`;

    return rel_path;
  }

  const INPUT_HEIGHT = 56;
  const CONTENT_MARGIN = 32;
  const SEE_MORE_HEIGHT = 24;
  const BORDER = 2;
  const EVENT_HEIGHT = 117;
  const LANDING_PAGE_HEIGHT = 85;
  const LANDING_PAGE_GAP = 16;
  const DEFAULT = 58;

  const hasLandingPages =
    !!results &&
    Array.isArray(results.LandingPages) &&
    results.LandingPages.length > 0;
  const hasEvents =
    !!results && Array.isArray(results.Events) && results.Events.length > 0;
  const hasResults = hasLandingPages || hasEvents;

  const landingPages = hasLandingPages ? results.LandingPages : [];
  const events = hasEvents ? results.Events : [];

  const raw_search_box_height = hasResults
    ? INPUT_HEIGHT +
      CONTENT_MARGIN +
      SEE_MORE_HEIGHT +
      BORDER +
      Math.max(
        events.length * EVENT_HEIGHT,
        landingPages.length * LANDING_PAGE_HEIGHT +
          LANDING_PAGE_GAP * (landingPages.length - 1)
      )
    : DEFAULT;
  const search_box_height =
    windowSize.height && windowSize.width
      ? Math.min(
          windowSize.height * (windowSize.width >= 1024 ? 0.9 : 0.8),
          raw_search_box_height
        )
      : DEFAULT;

  const search_box_top = windowSize.height
    ? windowSize.height / 2 - search_box_height / 2
    : 0;

  useEffect(() => {
    const search_box_element = document.getElementById("search_box");
    if (search_box_element) {
      animate(
        search_box_element,
        { top: `${search_box_top}px` },
        { delay: !hasResults ? 0.2 : 0 }
      );
    }
  }, [search_box_top, hasResults]);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) {
          setQuery("");
        }
      }}
    >
      <Dialog.Trigger asChild>
        {in_jumbotron ? (
          <motion.button
            layoutId="search_box"
            transition={{ duration: 0.2 }}
            className="relative flex items-center w-64 pl-8 pr-4 py-2 rounded-md bg-white text-gray-500 focus:outline-none focus:ring focus:ring-indigo-500"
          >
            {t("searchevents")}
            <MagnifyingGlassIcon
              className="absolute left-2 text-gray-500 w-4 h-4"
              aria-hidden="true"
            />
          </motion.button>
        ) : (
          <button aria-label={t("searchevents")} className="w-full lg:w-fit">
            {icon ?? (
              <div className="flex items-center gap-1 pl-4 pr-8 py-2 bg-gray-700 rounded-lg">
                <MagnifyingGlassIcon
                  className="text-white h-5 w-5"
                  aria-hidden="true"
                />
                <span className="text-sm text-gray-300">
                  {t("searchevents")}
                </span>
              </div>
            )}
          </button>
        )}
      </Dialog.Trigger>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                id="search_box_overlay"
                layoutId={in_jumbotron && open ? "search_box" : undefined}
                className="fixed inset-0 bg-black/0 z-50"
              />
            </Dialog.Overlay>
            <Dialog.Content asChild forceMount>
              <motion.div
                id="search_box"
                layoutId={in_jumbotron && open ? "search_box" : undefined}
                style={{
                  top: `${search_box_top}px`,
                }}
                initial={!in_jumbotron ? { opacity: 0 } : undefined}
                animate={!in_jumbotron ? { opacity: 1 } : undefined}
                exit={!in_jumbotron ? { opacity: 0 } : undefined}
                transition={{ duration: 0.2 }}
                className={joinClassNames(
                  "fixed z-[9999] max-h-[80dvh] lg:max-h-[90vh] w-[calc(100vw-1rem)] lg:w-[48rem] bg-white rounded-2xl overflow-y-auto shadow-modal border border-gray-200",
                  `left-[calc(50%-50vw+0.5rem)] lg:left-[calc(50%-24rem)]`
                )}
              >
                <Dialog.Title className="sr-only">
                  {t("searchevents")}
                </Dialog.Title>
                <Dialog.Description className="sr-only">
                  {t("description")}
                </Dialog.Description>
                <motion.div
                  key={isLoading ? "loading" : "searching"}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {isLoading ? (
                    <LoadingSpinner
                      size="18"
                      classes="absolute top-[18px] left-[18px]"
                    />
                  ) : (
                    <MagnifyingGlassIcon
                      className="absolute left-4 top-4 h-6 w-6 text-gray-500"
                      aria-hidden="true"
                    />
                  )}
                </motion.div>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    setQuery("");
                    const { search_query } = Object.fromEntries(
                      new FormData(e.currentTarget)
                    );
                    router.push(`/search?q=${search_query}`);
                    setOpen(false);
                    if (onClick) {
                      onClick();
                    }
                  }}
                >
                  <input
                    name="search_query"
                    aria-label={t("searchevents")}
                    placeholder={t("searchevents")}
                    className="w-full py-4 pl-12 pr-20 focus:outline-none rounded-2xl"
                    defaultValue={query}
                    autoComplete="off"
                    onInput={(e) => debouncedSetQuery(e.currentTarget.value)}
                  />
                </form>
                <span
                  className={joinClassNames(
                    "hidden lg:block absolute right-12 top-2.5 py-1.5 px-2 text-sm font-semibold bg-indigo-100 text-indigo-800 rounded-lg transition-opacity ease-in-out duration-200",
                    !query ? "opacity-0" : "opacity-100"
                  )}
                >
                  {t.rich("pressenter", {
                    kbd: (chunks) => (
                      <kbd className="inline-block font-normal p-px pb-1 rounded bg-gray-300 text-gray-800">
                        <span className="rounded bg-gray-100 px-1 py-0.5">
                          {chunks}
                        </span>
                      </kbd>
                    ),
                  })}
                </span>
                <Dialog.Close
                  aria-label={t("close")}
                  className="absolute right-4 top-4 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-600"
                >
                  <XMarkIcon
                    className="h-6 w-6 text-gray-500"
                    aria-hidden="true"
                  />
                </Dialog.Close>
                <AnimatePresence>
                  {hasResults && (
                    <motion.div
                      initial="hide"
                      animate="show"
                      exit="hide"
                      variants={{
                        hide: {
                          height: 0,
                          marginTop: 0,
                          marginBottom: 0,
                          opacity: 0,
                          transition: {
                            height: { delay: 0.2 },
                            marginTop: { delay: 0.2 },
                            marginBottom: { delay: 0.2 },
                          },
                        },
                        show: {
                          height: "auto",
                          marginTop: "1rem",
                          marginBottom: "1rem",
                          opacity: 1,
                          transition: { opacity: { delay: 0.2 } },
                        },
                      }}
                      className={joinClassNames(
                        "mx-4 flex flex-col lg:grid lg:grid-flow-dense gap-4 rounded-xl overflow-y-auto lg:overflow-hidden",
                        hasLandingPages ? "lg:grid-cols-2" : "lg:grid-cols-1"
                      )}
                    >
                      <div
                        className={joinClassNames(
                          "flex flex-col gap-2 p-px lg:max-h-[calc(90vh-88px)] lg:overflow-y-auto",
                          hasLandingPages ? "lg:col-start-2" : ""
                        )}
                      >
                        {hasEvents
                          ? events.map((event) => (
                              <Link
                                key={`${event.Name}-${event.Venue}-${event.Date}-event`}
                                href={
                                  event.Url ? getRelativePath(event.Url) : "#"
                                }
                                onClick={() => {
                                  setOpen(false);
                                  if (onClick) {
                                    onClick();
                                  }
                                }}
                                className="flex gap-2 w-full hover:bg-indigo-100 transition-colors ease-in-out duration-200 rounded-xl p-1"
                              >
                                {event.ImageUrl ? (
                                  <Image
                                    src={event.ImageUrl.trim()}
                                    alt={event.Name}
                                    width={86}
                                    height={100}
                                    className="rounded-lg h-full aspect-[6/7]"
                                  />
                                ) : (
                                  <div>
                                    <div className="flex items-center justify-center w-[86px] h-full aspect-[6/7] bg-indigo-50 rounded-lg">
                                      <MegaphoneIcon className="w-1/2 h-1/2 text-indigo-800" />
                                    </div>
                                  </div>
                                )}
                                <div className="flex flex-col gap-0.5">
                                  <p className="font-semibold">{event.Name}</p>
                                  <p className="text-gray-500">{event.Date}</p>
                                  <p className="text-indigo-500">
                                    {event.Venue}
                                  </p>
                                </div>
                              </Link>
                            ))
                          : null}
                        <Link
                          href={`/search?q=${query}`}
                          onClick={() => {
                            setOpen(false);
                            if (onClick) {
                              onClick();
                            }
                            setQuery("");
                          }}
                          className="flex items-center gap-1 text-indigo-500"
                        >
                          <p>{t("seemore")}</p>
                          <ArrowRightIcon
                            className="h-4 w-4"
                            aria-hidden="true"
                          />
                        </Link>
                      </div>
                      {hasLandingPages ? (
                        <div className="flex flex-col gap-4 p-px lg:max-h-[calc(90vh-88px)] lg:overflow-y-auto">
                          {landingPages.map((landingPage, index) => (
                            <LandingPageLinkV2
                              key={`${landingPage.Name}-${index}-landing-page`}
                              slug={
                                landingPage.Url
                                  ? landingPage.Url.replace(
                                      "https://www.iticket.co.nz",
                                      ""
                                    )
                                  : "#"
                              }
                              image={landingPage.ImageUrl}
                              name={landingPage.Name}
                              showNameOnMobile={true}
                              onClick={() => {
                                setOpen(false);
                                if (onClick) {
                                  onClick();
                                }
                              }}
                            />
                          ))}
                        </div>
                      ) : null}
                    </motion.div>
                  )}
                </AnimatePresence>
                {results && landingPages.length === 0 && events.length === 0 ? (
                  <div className="m-4">
                    <p className="text-lg font-semibold text-black">
                      {t("noresult")}
                    </p>
                  </div>
                ) : (
                  <></>
                )}
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
      <AnimatePresence>
        {open && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.95 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.125 }}
                className="fixed inset-0 bg-black/40 z-50"
              />
            </Dialog.Overlay>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}
